package ai.wagemix.net.models

import ai.wagemix.net.WMDescribable
import ai.wagemix.net.WMIdentifiable
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

interface WMOccupationTaxonomy {

    val root: Root
    val items: List<Entry>

    interface Item: WMIdentifiable<Int>, WMDescribable {
        val level: Int
    }
    interface Root: Item {
        override val level: Int
            get() = 0
        companion object
    }
    interface Entry: Item {
        val parentId: Int
        companion object
    }
    interface Tree {
        val item: Item
        val children: List<Tree>
    }
    interface Builder<R:Root, E:Entry, B:Builder<R, E, B>> {
        fun root(root: R): B
        fun addEntry(entry: E): B

        companion object
    }

    companion object
}

fun WMOccupationTaxonomyTree(
    item: WMOccupationTaxonomy.Item,
    children: List<WMOccupationTaxonomy.Tree>
): WMOccupationTaxonomy.Tree {
    return WMOccupationTaxonomyTreeData(item = item, children = children)
}

fun WMOccupationTaxonomy.asTree(): WMOccupationTaxonomy.Tree = buildTreeOf(root, childLevelEntries = items)

fun WMOccupationTaxonomy.Tree.toList(): List<WMOccupationTaxonomy.Item> {
    val result = mutableListOf<WMOccupationTaxonomy.Item>()
    fun traverse(tree: WMOccupationTaxonomy.Tree) {
        result.add(tree.item)
        tree.children.forEach { traverse(it) }
    }
    traverse(this)
    return result
}

fun WMOccupationTaxonomy.Tree.findById(id: Int): WMOccupationTaxonomy.Tree? {
    if (item.id == id) {
        return this
    }
    for (child in children) {
        val found = child.findById(id)
        if (found != null) {
            return found
        }
    }
    return null
}

private fun buildTreeOf(
    item: WMOccupationTaxonomy.Item,
    childLevelEntries: List<WMOccupationTaxonomy.Entry>
): WMOccupationTaxonomy.Tree {
    if (childLevelEntries.isEmpty()) {
        return WMOccupationTaxonomyTree(item, emptyList())
    }

    val childEntries = mutableListOf<WMOccupationTaxonomy.Entry>()
    val nextChildLevel = mutableListOf<WMOccupationTaxonomy.Entry>()
    childLevelEntries.forEach {
        when (it.parentId) {
            item.id -> childEntries.add(it)
            else -> if (it.level > item.level + 1) {
                nextChildLevel.add(it)
            }
        }
    }
    val children = childEntries.map { buildTreeOf(it, childLevelEntries = nextChildLevel) }
    return WMOccupationTaxonomyTree(item, children)
}

val WMOccupationTaxonomy.Item.name: String
    get() = description

val WMOccupationTaxonomy.Tree.count: Int
    get() = children.sumOf { it.count } + 1

fun WMOccupationTaxonomy.Companion.kxs(
    root: WMOccupationTaxonomy.Root,
    items: List<WMOccupationTaxonomy.Entry>
): KxsWMOccupationTaxonomy {
    return KxsWMOccupationTaxonomy(root = root.toKxs(), items = items.map { it.toKxs() })
}

fun WMOccupationTaxonomy.Entry.Companion.kxs(
    description: String,
    id: Int,
    level: Int,
    parentId: Int,
    code: String? = null
): KxsWMOccupationTaxonomy.Entry {
    return KxsWMOccupationTaxonomy.Entry(description = description, id = id, level = level, parentId = parentId, code = code)
}

fun WMOccupationTaxonomy.Root.Companion.kxs(
    description: String,
    id: Int,
    code: String? = null
): KxsWMOccupationTaxonomy.Root {
    return KxsWMOccupationTaxonomy.Root(description = description, id = id, code = code)
}

fun WMOccupationTaxonomy.Companion.kxsBuilder(): KxsWMOccupationTaxonomy.Builder {
    return KxsWMOccupationTaxonomy.Builder()
}

fun WMOccupationTaxonomy.Entry.toKxs(): KxsWMOccupationTaxonomy.Entry = when (this) {
    is KxsWMOccupationTaxonomy.Entry -> this
    else -> KxsWMOccupationTaxonomy.Entry(description = description, id = id, level = level, parentId = parentId)
}

fun WMOccupationTaxonomy.Root.toKxs(): KxsWMOccupationTaxonomy.Root = when (this) {
    is KxsWMOccupationTaxonomy.Root -> this
    else -> KxsWMOccupationTaxonomy.Root(description = description, id = id)
}

@Serializable
@ConsistentCopyVisibility
@SerialName("OccupationTaxonomy")
data class KxsWMOccupationTaxonomy internal constructor(
    override val root: Root,
    override val items: List<Entry>
): WMOccupationTaxonomy {

    @Serializable
    @ConsistentCopyVisibility
    @SerialName("OccupationTaxonomyRoot")
    data class Root internal constructor(
        override val id: Int,
        override val description: String,
        /** The SOC code (e.g. "11-9031"); absent from API releases before the site rebuild. */
        val code: String? = null
    ): WMOccupationTaxonomy.Root

    @Serializable
    @ConsistentCopyVisibility
    @SerialName("OccupationTaxonomyEntry")
    data class Entry internal constructor(
        override val id: Int,
        override val description: String,
        override val level: Int,
        @SerialName("parent_id") override val parentId: Int,
        /** The SOC code (e.g. "11-9031"); absent from API releases before the site rebuild. */
        val code: String? = null
    ): WMOccupationTaxonomy.Entry

    class Builder internal constructor(): WMOccupationTaxonomy.Builder<Root, Entry, Builder> {
        private lateinit var root: Root
        private val entries = mutableMapOf<Int, Entry>()

        override fun root(root: Root): Builder {
            check(root.level == 0) { "Root level must be 0" }
            this.root = root
            return this
        }

        override fun addEntry(entry: Entry): Builder {
            check(entry.level > 0) { "Entry level must be greater than 0" }
            entries[entry.id] = entry
            return this
        }

        fun build(): KxsWMOccupationTaxonomy {
            val items = mutableListOf<Entry>()
            entries.values.sortedBy { it.id }.forEach { entry ->
                if (entry.level == 1) {
                    check(entry.parentId == root.id) { "Entry level 1 must have root as parent" }
                } else {
                    val parent = entries[entry.parentId]
                    check(parent != null) { "Parent entry not found for entry id: ${entry.id}" }
                    check(parent.level + 1 == entry.level) { "Entry level must be one greater than parent level" }
                }
                items.add(entry)
            }
            return KxsWMOccupationTaxonomy(root = root, items = entries.values.toList())
        }
    }
}

fun WMOccupationTaxonomy.toKxs(): KxsWMOccupationTaxonomy = when (this) {
    is KxsWMOccupationTaxonomy -> this
    else -> KxsWMOccupationTaxonomy(root = root.toKxs(), items = items.map { it.toKxs() })
}

private data class WMOccupationTaxonomyTreeData(
    override val item: WMOccupationTaxonomy.Item,
    override val children: List<WMOccupationTaxonomy.Tree>
): WMOccupationTaxonomy.Tree
